import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import type { PropertyValue } from '@skop-app/lib-domain/types'
import { ReviewStatus } from '@skop-app/lib-types'

import { useUpsertTranslation } from '@/services/mutations'
import { useCreateField } from '@/services/property'
import { useUserContext } from '@/context'
import type {
  CreateFieldInput,
  CreateOptionInput,
  TranslationInput,
} from '@/types'
import { duplicateOptionsWithTranslations } from '@/services/property/option.normalized'
import { fetchFieldItemFn } from '@/services/property/option.query'
import { useUpsertOption } from '@/services/property/option.mutation'
import { listQueryKey } from '@/services/property/property.keys'
import { resolveCreationReviewPolicy } from '@/features/reviews'

export type FieldOutput = CreateFieldInput & {
  translations: TranslationInput[]
  options: FieldOutput[]
}
export type OptionOutput = CreateOptionInput & {
  translations: TranslationInput[]
  options: OptionOutput[]
}

export type PropertyOutput = Omit<CreateOptionInput, 'status'> & {
  translations: TranslationInput[]
  options: PropertyOutput[]
}

const flattenImportedOptions = (options: PropertyOutput[]): PropertyOutput[] =>
  options.flatMap((option) => [
    option,
    ...flattenImportedOptions(option.options ?? []),
  ])

export const useCreateOptions = () => {
  const { mutateAsync: upsertOption } = useUpsertOption()
  const { mutateAsync: upsertTranslation } = useUpsertTranslation()
  const { mutateAsync: createField } = useCreateField()
  const {
    user,
    ownership,
    isAdmin,
    isSkopAdmin,
    organizationScope,
    teamReviewWorkflowPolicy,
  } = useUserContext()
  const queryClient = useQueryClient()
  const activeOrganizationId = organizationScope.activeOrganizationId

  const importOptions = async ({
    fieldOptions,
    fieldId,
    fieldKey: _fieldKey,
    listOwnerOrganizationID,
  }: {
    fieldOptions: PropertyOutput[]
    fieldId: string
    fieldKey: string
    listOwnerOrganizationID?: string
  }) => {
    const { elementStatus: status } = ownership
      ? resolveCreationReviewPolicy({
          elementType: 'option',
          source: 'import',
          ownership,
          listOrganizationID: listOwnerOrganizationID,
          isAdmin,
          isSkopAdmin,
          teamReviewWorkflowPolicy,
        })
      : {
          elementStatus: 'ACCEPTED' as const,
        }
    const targetOrganizationID =
      activeOrganizationId ??
      ownership?.organizationID ??
      process.env.NEXT_PUBLIC_SKOP_ORGID ??
      ''
    const fields = flattenImportedOptions(fieldOptions)

    await Promise.all(
      fields.map(
        async ({ translations: _, options: __, updatedAt: ___, ...rest }) => {
          await upsertOption({
            upsertType: 'create',
            input: {
              ...rest,
              value: rest.value ?? rest.label,
              status,
              userID: user?.id ?? rest.userID ?? '',
              organizationID: targetOrganizationID,
            },
            queryKey: listQueryKey(fieldId, undefined, 'picker'),
          })
        },
      ),
    )

    await Promise.all(
      fields
        .flatMap((field) => field.translations)
        .map(async (translation) => {
          await upsertTranslation({
            input: translation,
            upsertType: 'create',
          })
        }),
    )

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['list', fieldId],
        refetchType: 'all',
      }),
      queryClient.invalidateQueries({
        queryKey: ['optionsByField', fieldId],
        refetchType: 'all',
      }),
    ])
  }

  type DuplicateOptionsGroupParams = {
    data: {
      id: string
      source?: string
    }
  }

  /**
   * Duplicate an entire options group (field and nested options) under the current team.
   * Fetches the source list options directly to avoid passing pre-flattened items around.
   */
  const duplicateOptionsGroup = useCallback(
    async ({ data }: DuplicateOptionsGroupParams) => {
      const { normalized, supportedKey } = await fetchFieldItemFn({
        optionId: data.id,
        optionFilter: {
          status: { eq: ReviewStatus.ACCEPTED },
        },
        lang: undefined,
      })

      const newFieldId = uuid()
      const targetOrganizationID =
        activeOrganizationId ?? process.env.NEXT_PUBLIC_SKOP_ORGID ?? ''
      const { elementStatus: status } = ownership
        ? resolveCreationReviewPolicy({
            elementType: 'option',
            source: 'duplicate',
            ownership,
            listOrganizationID: targetOrganizationID,
            isAdmin,
            isSkopAdmin,
            teamReviewWorkflowPolicy,
          })
        : {
            elementStatus: 'ACCEPTED' as const,
          }
      const sourceSuffix =
        typeof data.source === 'string' && data.source.length > 0
          ? ` - ${data.source}`
          : ''
      const fieldInput: CreateFieldInput = {
        id: newFieldId,
        label: sourceSuffix
          ? supportedKey.label.replace(sourceSuffix, '')
          : supportedKey.label,
        key: supportedKey.key ?? '',
        value: String(supportedKey.value ?? ''),
        type: 'LIST' as PropertyValue,
        organizationFieldsId: targetOrganizationID,
      }

      const { options, translations } = duplicateOptionsWithTranslations({
        normalized,
        newFieldId,
        targetStatus: status,
        targetUserID: user?.id ?? ownership?.byUserID ?? '',
        targetOrganizationID,
      })

      await createField(fieldInput)

      await Promise.all(
        options.map((option) =>
          upsertOption({
            upsertType: 'create',
            input: option,
            queryKey: listQueryKey(newFieldId, undefined, 'picker'),
          }),
        ),
      )

      await Promise.all(
        translations.map((translation) =>
          upsertTranslation({
            input: translation,
            upsertType: 'create',
          }),
        ),
      )

      queryClient.invalidateQueries({ queryKey: ['lists-all'] })
    },
    [
      createField,
      upsertOption,
      upsertTranslation,
      user?.id,
      ownership,
      isAdmin,
      isSkopAdmin,
      teamReviewWorkflowPolicy,
      activeOrganizationId,
      queryClient,
    ],
  )

  return {
    importOptions,
    duplicateOptionsGroup,
  }
}
